// Wraps an Osteotomy-Height annotated frame in the unified CADtomie report
// chrome. Mirrors the structure of buildAngleReport so all single-frame
// exports look like siblings.

import { buildReport } from './reportFrame';
import { fmtMm } from './geometry';

export interface HeightReportInput {
  annotated: HTMLCanvasElement;
  /** Distance in image pixels. */
  distancePx: number;
  /** Distance in millimetres if calibration is active, else null. */
  distanceMm: number | null;
  /** Reference object length used during calibration, in mm (e.g. 1.6). */
  calibrationMm: number | null;
  timestamp: number;
}

const HEIGHT_GREEN = '#30d158';

export function buildHeightReport(input: HeightReportInput): HTMLCanvasElement {
  const calibrated = input.distanceMm !== null && input.calibrationMm !== null;
  const variant = calibrated
    ? `Calibrated · ${input.calibrationMm!.toFixed(1)} mm reference`
    : 'Uncalibrated · pixel distance';
  const result = calibrated
    ? `Distance: ${fmtMm(input.distanceMm!, 1)}`
    : `Distance: ${Math.round(input.distancePx)} px`;

  return buildReport(
    {
      module: 'height',
      variant,
      result,
      timestamp: input.timestamp,
    },
    (ctx, rect) => {
      const GAP = 32;
      const readoutW = 360;
      const imgMaxW = rect.w - readoutW - GAP;
      const srcAR = input.annotated.height / input.annotated.width;
      const imgW = imgMaxW;
      const imgH = imgW * srcAR;

      const imgX = rect.x;
      const imgY = rect.y;
      ctx.save();
      roundedRectPath(ctx, imgX, imgY, imgW, imgH, 16);
      ctx.clip();
      ctx.drawImage(input.annotated, imgX, imgY, imgW, imgH);
      ctx.restore();
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      roundedRectPath(ctx, imgX + 0.5, imgY + 0.5, imgW - 1, imgH - 1, 16);
      ctx.stroke();

      // Readout panel
      const panelX = imgX + imgW + GAP;
      const panelY = imgY;
      const panelW = readoutW;
      const panelH = imgH;
      ctx.fillStyle = '#161618';
      roundedRectPath(ctx, panelX, panelY, panelW, panelH, 16);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      roundedRectPath(ctx, panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1, 16);
      ctx.stroke();

      const panelPad = 32;
      let py = panelY + panelPad + 4;

      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font =
        '600 14px -apple-system, "SF Pro Text", system-ui, sans-serif';
      ctx.fillText('MEASURED DISTANCE', panelX + panelPad, py);
      py += 28;

      ctx.fillStyle = '#ffffff';
      ctx.font =
        'bold 88px -apple-system, "SF Pro Display", system-ui, sans-serif';
      const big = calibrated
        ? `${input.distanceMm!.toFixed(1)}`
        : `${Math.round(input.distancePx)}`;
      ctx.fillText(big, panelX + panelPad, py);
      const bigW = ctx.measureText(big).width;

      ctx.font = '600 28px -apple-system, "SF Pro Display", system-ui, sans-serif';
      ctx.fillStyle = HEIGHT_GREEN;
      ctx.fillText(calibrated ? 'mm' : 'px', panelX + panelPad + bigW + 12, py + 50);
      py += 110;

      ctx.fillStyle = HEIGHT_GREEN;
      ctx.font = '600 14px -apple-system, system-ui, sans-serif';
      ctx.fillText(
        calibrated ? 'CALIBRATED' : 'NOT CALIBRATED',
        panelX + panelPad,
        py,
      );
      py += 22;

      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = '500 13px -apple-system, system-ui, sans-serif';
      const desc = calibrated
        ? `Scale derived from a ${input.calibrationMm!.toFixed(1)} mm coplanar reference. Verify the reference plane matches the measurement plane to avoid parallax error.`
        : 'No reference object set. The measurement is in image pixels — calibrate against a known coplanar object to convert to mm.';
      wrapText(ctx, desc, panelX + panelPad, py, panelW - panelPad * 2, 18);

      return imgH;
    },
  );
}

/* helpers */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lineH: number,
) {
  const words = text.split(/\s+/);
  let line = '';
  let yy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      yy += lineH;
      line = w;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
